import { assertExists } from '@blocksuite/global/utils';
import {
  ArrowDownSmallIcon,
  DeleteIcon,
  InvisibleIcon,
  MoreHorizontalIcon,
  PlusIcon,
  TagsIcon,
  ViewIcon,
} from '@blocksuite/icons/rc';
import type { DragEndEvent, DraggableAttributes } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  PiqueLogo,
  type MenuProps,
  Tooltip,
} from '@pique/component';
import { SharePageButton } from '@pique/core/components/pique/share-page-modal';
import { useEditCollectionName } from '@pique/core/components/page-list';
import { createTitleFilter } from '@pique/core/components/page-list/filter/utils';
import { createEmptyCollection } from '@pique/core/components/page-list/use-collection-manager';
import { CollectionService } from '@pique/core/modules/collection';
import { DocLinksService } from '@pique/core/modules/doc-link';
import { EditorService } from '@pique/core/modules/editor';
import { EditorSettingService } from '@pique/core/modules/editor-settting';
import { WorkspacePropertiesAdapter } from '@pique/core/modules/properties';
import type {
  PageInfoCustomProperty,
  PageInfoCustomPropertyMeta,
  PagePropertyType,
} from '@pique/core/modules/properties/services/schema';
import { TagService } from '@pique/core/modules/tag';
import { WorkbenchService } from '@pique/core/modules/workbench';
import { canUseEdgelessSplit } from '@pique/core/modules/workbench/entities/workbench-layout-policy';
import { ViewService } from '@pique/core/modules/workbench/services/view';
import type { Collection } from '@pique/env/filter';
import { i18nTime, useI18n } from '@pique/i18n';
import {
  useLiveData,
  useService,
  useServices,
  WorkspaceService,
} from '@toeverything/infra';
import clsx from 'clsx';
import { useDebouncedValue } from 'foxact/use-debounced-value';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { CalendarDaysIcon, FolderOpenIcon } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useTheme } from 'next-themes';
import type React from 'react';
import type {
  CSSProperties,
  MouseEvent,
  MouseEventHandler,
  PropsWithChildren,
} from 'react';
import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { isMobileView } from '../../app-sidebar/index.jotai';
import { PiquePageReference } from '../reference-link';
import { ManagerContext, useManager } from './common';
import { ConfirmDeletePropertyModal } from './confirm-delete-property-modal';
import type { PagePropertyIcon } from './icons-mapping';
import { getDefaultIconName, nameToIcon } from './icons-mapping';
import { PageOverviewRow } from './meeting-overview';
import { MindmapMini } from './mindmap-mini';
import type { MenuItemOption } from './menu-items';
import {
  EditPropertyNameMenuItem,
  PropertyTypeMenuItem,
  renderMenuItemOptions,
} from './menu-items';
import type { PagePropertiesMetaManager } from './page-properties-manager';
import {
  newPropertyTypes,
  PagePropertiesManager,
} from './page-properties-manager';
import {
  AttendeesValue,
  propertyValueRenderers,
  TagsValue,
} from './property-row-value-renderer';
import * as styles from './styles.css';

type PagePropertiesSettingsPopupProps = PropsWithChildren<{
  className?: string;
  style?: React.CSSProperties;
}>;

const Divider = memo(function Divider() {
  return <div className={styles.tableHeaderDivider} />;
});

interface TimestampDisplayProps {
  docId: string;
  createDate?: number;
  updatedDate?: number;
}

const TimestampDisplay = memo(function TimestampDisplay({
  docId,
  createDate,
  updatedDate,
}: TimestampDisplayProps) {
  const t = useI18n();
  const workspaceService = useService(WorkspaceService);

  const docStateObservable = useMemo(
    () => workspaceService.workspace.engine.doc.docState$(docId),
    [workspaceService.workspace.engine.doc, docId]
  );
  const docState = useLiveData(docStateObservable);
  const debouncedDocState = useDebouncedValue(docState, 2000);
  const { syncing, retrying, serverClock } = debouncedDocState ?? {};

  const localizedCreateTime = createDate ? i18nTime(createDate) : null;

  const createTimeElement = (
    <div className={styles.tableHeaderTimestamp}>
      {t['Created']()} {localizedCreateTime}
    </div>
  );

  if (serverClock) {
    return (
      <Tooltip
        side="right"
        content={
          <>
            <div className={styles.tableHeaderTimestamp}>
              {t['Updated']()} {i18nTime(serverClock)}
            </div>
            {createDate && (
              <div className={styles.tableHeaderTimestamp}>
                {t['Created']()} {i18nTime(createDate)}
              </div>
            )}
          </>
        }
      >
        <div className={styles.tableHeaderTimestamp}>
          {!syncing && !retrying ? (
            <>
              {t['Updated']()}{' '}
              {i18nTime(serverClock, {
                relative: {
                  max: [1, 'day'],
                  accuracy: 'minute',
                },
                absolute: {
                  accuracy: 'day',
                },
              })}
            </>
          ) : (
            <>{t['com.pique.syncing']()}</>
          )}
        </div>
      </Tooltip>
    );
  }

  if (updatedDate) {
    return (
      <Tooltip side="right" content={createTimeElement}>
        <div className={styles.tableHeaderTimestamp}>
          {t['Updated']()} {i18nTime(updatedDate)}
        </div>
      </Tooltip>
    );
  }

  return createTimeElement;
});

type PropertyVisibility = PageInfoCustomProperty['visibility'];

const editingPropertyAtom = atom<string | null>(null);

const modifiers = [restrictToParentElement, restrictToVerticalAxis];

interface SortablePropertiesProps {
  children: (properties: PageInfoCustomProperty[]) => React.ReactNode;
}

export const SortableProperties = ({ children }: SortablePropertiesProps) => {
  const manager = useManager();
  const properties = useMemo(() => manager.sorter.getOrderedItems(), [manager]);
  const editingItem = useAtomValue(editingPropertyAtom);
  const draggable = !manager.readonly && !editingItem;
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  // use localProperties since changes applied to upstream may be delayed
  // if we use that one, there will be weird behavior after reordering
  const [localProperties, setLocalProperties] = useState(properties);

  useEffect(() => {
    setLocalProperties(properties);
  }, [properties]);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!draggable) {
        return;
      }
      const { active, over } = event;
      if (over) {
        manager.sorter.move(active.id, over.id);
      }
      setLocalProperties(manager.sorter.getOrderedItems());
    },
    [manager, draggable]
  );

  const filteredProperties = useMemo(
    () => localProperties.filter(p => manager.getCustomPropertyMeta(p.id)),
    [localProperties, manager]
  );

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd} modifiers={modifiers}>
      <SortableContext disabled={!draggable} items={properties}>
        {children(filteredProperties)}
      </SortableContext>
    </DndContext>
  );
};

type SyntheticListenerMap = ReturnType<typeof useSortable>['listeners'];

const SortablePropertyRow = ({
  property,
  className,
  children,
  ...props
}: {
  property: PageInfoCustomProperty;
  className?: string;
  children?:
    | React.ReactNode
    | ((props: {
        attributes: DraggableAttributes;
        listeners?: SyntheticListenerMap;
      }) => React.ReactNode);
}) => {
  const manager = useManager();
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    active,
    isDragging,
    isSorting,
  } = useSortable({
    id: property.id,
  });
  const style: CSSProperties = useMemo(
    () => ({
      transform: transform
        ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
        : undefined,
      transition: isSorting ? transition : undefined,
      pointerEvents: manager.readonly ? 'none' : undefined,
    }),
    [isSorting, manager.readonly, transform, transition]
  );

  return (
    <div
      style={style}
      ref={setNodeRef}
      className={clsx(styles.propertyRow, className)}
      data-property={property.id}
      data-draggable={!manager.readonly}
      data-dragging={isDragging}
      data-other-dragging={active ? active.id !== property.id : false}
      {...props}
      {...attributes}
      {...listeners}
    >
      {typeof children === 'function'
        ? children({ attributes, listeners })
        : children}
    </div>
  );
};

const visibilities: PropertyVisibility[] = ['visible', 'hide', 'hide-if-empty'];
const rotateVisibility = (
  visibility: PropertyVisibility
): PropertyVisibility => {
  const index = visibilities.indexOf(visibility);
  return visibilities[(index + 1) % visibilities.length] ?? 'visible';
};

const visibilityMenuText = (visibility: PropertyVisibility = 'visible') => {
  switch (visibility) {
    case 'hide':
      return 'com.pique.page-properties.property.hide-in-view';
    case 'hide-if-empty':
      return 'com.pique.page-properties.property.hide-in-view-when-empty';
    case 'visible':
      return 'com.pique.page-properties.property.show-in-view';
    default:
      throw new Error(`unknown visibility: ${visibility}`);
  }
};

const visibilitySelectorText = (visibility: PropertyVisibility = 'visible') => {
  switch (visibility) {
    case 'hide':
      return 'com.pique.page-properties.property.always-hide';
    case 'hide-if-empty':
      return 'com.pique.page-properties.property.hide-when-empty';
    case 'visible':
      return 'com.pique.page-properties.property.always-show';
    default:
      throw new Error(`unknown visibility: ${visibility}`);
  }
};

const VisibilityModeSelector = ({
  property,
}: {
  property: PageInfoCustomProperty;
}) => {
  const manager = useManager();
  const t = useI18n();
  const meta = manager.getCustomPropertyMeta(property.id);
  const visibility = property.visibility || 'visible';

  const menuItems = useMemo(() => {
    const options: MenuItemOption[] = [];
    options.push(
      visibilities.map(v => {
        const text = visibilityMenuText(v);
        return {
          text: t[text](),
          selected: visibility === v,
          onClick: () => {
            manager.updateCustomProperty(property.id, {
              visibility: v,
            });
          },
        };
      })
    );
    return renderMenuItemOptions(options);
  }, [manager, property.id, t, visibility]);

  if (!meta) {
    return null;
  }

  const required = meta.required;

  return (
    <Menu
      items={menuItems}
      rootOptions={{
        open: required ? false : undefined,
      }}
      contentOptions={{
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      }}
    >
      <div
        role="button"
        data-required={required}
        className={styles.selectorButton}
      >
        {required ? (
          t['com.pique.page-properties.property.required']()
        ) : (
          <>
            {t[visibilitySelectorText(visibility)]()}
            <ArrowDownSmallIcon width={16} height={16} />
          </>
        )}
      </div>
    </Menu>
  );
};

export const PagePropertiesSettingsPopup = ({
  children,
}: PagePropertiesSettingsPopupProps) => {
  const manager = useManager();
  const t = useI18n();

  const menuItems = useMemo(() => {
    const options: MenuItemOption[] = [];
    options.push(
      <div
        role="heading"
        className={styles.menuHeader}
        style={{ minWidth: 320 }}
      >
        {t['com.pique.page-properties.settings.title']()}
      </div>
    );
    options.push('-');
    options.push([
      <SortableProperties key="sortable-settings">
        {properties =>
          properties.map(property => {
            const meta = manager.getCustomPropertyMeta(property.id);
            assertExists(meta, 'meta should exist for property');
            const Icon = nameToIcon(meta.icon, meta.type);
            const name = meta.name;
            return (
              <SortablePropertyRow
                key={meta.id}
                property={property}
                className={styles.propertySettingRow}
                data-testid="page-properties-settings-menu-item"
              >
                <Icon />
                <div
                  data-testid="page-property-setting-row-name"
                  className={styles.propertyRowName}
                >
                  {name}
                </div>
                <VisibilityModeSelector property={property} />
              </SortablePropertyRow>
            );
          })
        }
      </SortableProperties>,
    ]);
    return renderMenuItemOptions(options);
  }, [manager, t]);

  return (
    <Menu
      contentOptions={{
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      }}
      items={menuItems}
    >
      {children}
    </Menu>
  );
};

type PageBacklinksPopupProps = PropsWithChildren<{
  backlinks: { docId: string; blockId: string; title: string }[];
}>;

export const PageBacklinksPopup = ({
  backlinks,
  children,
}: PageBacklinksPopupProps) => {
  const manager = useManager();

  return (
    <Menu
      contentOptions={{
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      }}
      items={
        <div className={styles.backlinksList}>
          {backlinks.map(link => (
            <PiquePageReference
              key={link.docId + ':' + link.blockId}
              wrapper={MenuItem}
              pageId={link.docId}
              docCollection={manager.workspace.docCollection}
            />
          ))}
        </div>
      }
    >
      {children}
    </Menu>
  );
};

interface PagePropertyRowNameProps {
  property: PageInfoCustomProperty;
  meta: PageInfoCustomPropertyMeta;
  editing: boolean;
  onFinishEditing: () => void;
}

export const PagePropertyRowNameMenu = ({
  editing,
  meta,
  property,
  onFinishEditing,
  children,
}: PropsWithChildren<PagePropertyRowNameProps>) => {
  const manager = useManager();
  const [localPropertyMeta, setLocalPropertyMeta] = useState(() => ({
    ...meta,
  }));
  const [localProperty, setLocalProperty] = useState(() => ({ ...property }));
  const nextVisibility = rotateVisibility(localProperty.visibility);

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setLocalPropertyMeta(meta);
  }, [meta]);

  useEffect(() => {
    setLocalProperty(property);
  }, [property]);

  const handleFinishEditing = useCallback(() => {
    onFinishEditing();
    manager.updateCustomPropertyMeta(meta.id, localPropertyMeta);
    manager.updateCustomProperty(property.id, localProperty);
  }, [
    localProperty,
    localPropertyMeta,
    manager,
    meta.id,
    onFinishEditing,
    property.id,
  ]);
  const t = useI18n();
  const handleNameBlur = useCallback(
    (v: string) => {
      manager.updateCustomPropertyMeta(meta.id, {
        name: v,
      });
    },
    [manager, meta.id]
  );
  const handleNameChange: (name: string) => void = useCallback(name => {
    setLocalPropertyMeta(prev => ({
      ...prev,
      name: name,
    }));
  }, []);
  const toggleHide = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      setLocalProperty(prev => ({
        ...prev,
        visibility: nextVisibility,
      }));
    },
    [nextVisibility]
  );
  const handleDelete = useCallback(() => {
    manager.removeCustomProperty(property.id);
  }, [manager, property.id]);

  const handleIconChange = useCallback(
    (icon: PagePropertyIcon) => {
      setLocalPropertyMeta(prev => ({
        ...prev,
        icon,
      }));
      manager.updateCustomPropertyMeta(meta.id, {
        icon: icon,
      });
    },
    [manager, meta.id]
  );

  const menuItems = useMemo(() => {
    const options: MenuItemOption[] = [];
    options.push(
      <EditPropertyNameMenuItem
        property={localPropertyMeta}
        onIconChange={handleIconChange}
        onNameBlur={handleNameBlur}
        onNameChange={handleNameChange}
      />
    );
    options.push(<PropertyTypeMenuItem property={localPropertyMeta} />);
    if (!localPropertyMeta.required) {
      options.push('-');
      options.push({
        icon:
          nextVisibility === 'hide' || nextVisibility === 'hide-if-empty' ? (
            <InvisibleIcon />
          ) : (
            <ViewIcon />
          ),
        text: t[
          visibilityMenuText(rotateVisibility(localProperty.visibility))
        ](),
        onClick: toggleHide,
      });
      options.push({
        type: 'danger',
        icon: <DeleteIcon />,
        text: t['com.pique.page-properties.property.remove-property'](),
        onClick: () => setShowDeleteModal(true),
      });
    }
    return renderMenuItemOptions(options);
  }, [
    handleIconChange,
    handleNameBlur,
    handleNameChange,
    localProperty.visibility,
    localPropertyMeta,
    nextVisibility,
    t,
    toggleHide,
  ]);

  return (
    <>
      <Menu
        rootOptions={{
          open: editing,
        }}
        contentOptions={{
          onInteractOutside: handleFinishEditing,
          onClick(e: React.MouseEvent) {
            e.stopPropagation();
          },
          onKeyDown(e: React.KeyboardEvent) {
            if (e.key === 'Escape') {
              handleFinishEditing();
            }
          },
        }}
        items={menuItems}
      >
        {children}
      </Menu>
      <ConfirmDeletePropertyModal
        onConfirm={() => {
          setShowDeleteModal(false);
          handleDelete();
        }}
        onCancel={() => setShowDeleteModal(false)}
        show={showDeleteModal}
        property={meta}
      />
    </>
  );
};

export const CollectionMenu = ({
  pageId,
  currentCollection,
  suggestedName,
  children,
}: PropsWithChildren<{
  pageId: string;
  currentCollection: Collection | null;
  suggestedName?: string;
}>) => {
  const t = useI18n();
  const { collectionService, workbenchService } = useServices({
    CollectionService,
    WorkbenchService,
  });
  const collections = useLiveData(collectionService.collections$);
  const { node, open: openCreateCollectionModel } = useEditCollectionName({
    title: t['com.pique.editCollection.createCollection'](),
    showTips: true,
  });

  const handleSelectCollection = useCallback(
    (collection: Collection) => {
      if (currentCollection?.id === collection.id) {
        collectionService.deletePageFromCollection(collection.id, pageId);
      } else {
        if (currentCollection) {
          collectionService.deletePageFromCollection(
            currentCollection.id,
            pageId
          );
        }
        collectionService.addPageToCollection(collection.id, pageId);
      }
    },
    [collectionService, currentCollection, pageId]
  );

  const handleCreateCollection = useCallback(() => {
    openCreateCollectionModel('')
      .then((name: string) => {
        const id = nanoid();
        const newCollection = createEmptyCollection(id, { name });
        collectionService.addCollection(newCollection);
        collectionService.addPageToCollection(id, pageId);
        workbenchService.workbench.openCollection(id);
      })
      .catch((err: Error) => {
        console.error(err);
      });
  }, [
    collectionService,
    openCreateCollectionModel,
    pageId,
    workbenchService.workbench,
  ]);

  const handleCreateSuggestedCollection = useCallback(() => {
    if (!suggestedName) return;
    const id = nanoid();
    const newCollection = createEmptyCollection(id, {
      name: suggestedName,
      filterList: [createTitleFilter([suggestedName])],
    });
    collectionService.addCollection(newCollection);
    workbenchService.workbench.openCollection(id);
  }, [collectionService, suggestedName, workbenchService.workbench]);

  const menuItems = useMemo(() => {
    const options: MenuItemOption[] = [];

    if (
      suggestedName &&
      !collections.some((c: Collection) => c.name === suggestedName)
    ) {
      options.push(
        <div
          key="suggested"
          role="menuitem"
          className={styles.suggestedCollectionItem}
          onClick={handleCreateSuggestedCollection}
        >
          <FolderOpenIcon className={styles.suggestedCollectionIcon} />
          <span className={styles.suggestedCollectionText}>
            {suggestedName}
          </span>
          <span className={styles.suggestedCollectionLabel}>suggested</span>
        </div>
      );
      options.push('-');
    }

    if (collections.length > 0) {
      options.push(
        collections.map((collection: Collection) => ({
          icon: <FolderOpenIcon className={styles.menuItemIconContainer} />,
          text: collection.name,
          selected: currentCollection?.id === collection.id,
          onClick: () => handleSelectCollection(collection),
        }))
      );
      options.push('-');
    }

    options.push({
      icon: <PlusIcon className={styles.menuItemIconContainer} />,
      text: t['com.pique.editCollection.createCollection'](),
      onClick: handleCreateCollection,
    });

    return renderMenuItemOptions(options);
  }, [
    collections,
    currentCollection?.id,
    handleSelectCollection,
    handleCreateSuggestedCollection,
    handleCreateCollection,
    suggestedName,
    t,
  ]);

  return (
    <>
      <Menu
        contentOptions={{
          onClick(e: React.MouseEvent) {
            e.stopPropagation();
          },
        }}
        items={menuItems}
      >
        {children}
      </Menu>
      {node}
    </>
  );
};

const SHARE_COMPACT_BREAKPOINT = 700;
const COLLECTION_HIDE_BREAKPOINT = 500;
const PIQUE_APP_STORE_URL =
  'https://apps.apple.com/us/app/pique-ai-note-taker/id6740837842';

const MobileMindmapModeRow = memo(function MobileMindmapModeRow({
  docId,
}: {
  docId?: string;
}) {
  const manager = useManager();
  const editorService = useService(EditorService);
  const viewService = useService(ViewService);
  const { resolvedTheme } = useTheme();
  const currentMode = useLiveData(editorService.editor.mode$);
  const isMindmapMode = currentMode === 'edgeless';
  const currentPageId = docId ?? manager.pageId;

  const handleToggleMobileMindmap = useCallback(() => {
    const nextMode = isMindmapMode ? 'page' : 'edgeless';
    viewService.view.setViewMode(nextMode);
    editorService.editor.setMode(nextMode);
  }, [editorService.editor, isMindmapMode, viewService.view]);

  const handleOpenAppStore = useCallback(() => {
    window.open(PIQUE_APP_STORE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  return (
    <div className={styles.mobileMindmapModeRow}>
      <Tooltip content={isMindmapMode ? 'Show notes' : 'Show mindmap'}>
        <button
          type="button"
          className={styles.mobileMindmapModeButton}
          data-active={isMindmapMode}
          aria-label={isMindmapMode ? 'Show notes' : 'Show mindmap'}
          aria-pressed={isMindmapMode}
          onClick={handleToggleMobileMindmap}
        >
          <MindmapMini
            seed={currentPageId}
            size={22}
            isDark={resolvedTheme === 'dark'}
            className={styles.mobileMindmapPreview}
          />
        </button>
      </Tooltip>
      <Button
        className={styles.mobileGetPiqueButton}
        prefix={<PiqueLogo className={styles.mobileGetPiqueLogo} />}
        onClick={handleOpenAppStore}
      >
        Get Pique!
      </Button>
    </div>
  );
});

const PagePropertiesMobileHeader = memo(function PagePropertiesMobileHeader({
  docId,
  framed = false,
}: {
  docId?: string;
  framed?: boolean;
}) {
  if (canUseEdgelessSplit()) {
    return null;
  }

  const content = (
    <>
      <MobileMindmapModeRow docId={docId} />
      {framed ? <Divider /> : null}
    </>
  );

  if (!framed) {
    return content;
  }

  return (
    <div className={styles.root}>
      <div className={styles.rootCentered}>{content}</div>
    </div>
  );
});

export const PagePropertiesTableHeader = memo(
  function PagePropertiesTableHeader({
    className,
    style,
  }: {
    className?: string;
    style?: React.CSSProperties;
  }) {
    const manager = useManager();
    const isMobileViewValue = useAtomValue(isMobileView);
    const headerRef = useRef<HTMLDivElement>(null);
    const containerWidthRef = useRef<number>(window.innerWidth);
    const [shareCompact, setShareCompact] = useState(
      () => window.innerWidth < SHARE_COMPACT_BREAKPOINT
    );
    const [hideCollection, setHideCollection] = useState(
      () => window.innerWidth < COLLECTION_HIDE_BREAKPOINT
    );
    const t = useI18n();
    const { docLinksServices, editorSettingService, collectionService } =
      useServices({
        DocLinksServices: DocLinksService,
        EditorSettingService,
        CollectionService,
      });

    const docBacklinks = docLinksServices.backlinks;
    const backlinks = useLiveData(docBacklinks.backlinks$);

    const settings = useLiveData(editorSettingService.editorSetting.settings$);

    const collections = useLiveData(collectionService.collections$);
    const collection = useMemo(() => {
      if (!collections || !manager.page) {
        return null;
      }
      const pageId = manager.page.id;
      return (
        collections.find((collection: Collection) =>
          collection.allowList.includes(pageId)
        ) ?? null
      );
    }, [collections, manager.page]);

    const suggestedCollectionName = useMemo(() => {
      if (collection) {
        return undefined;
      }
      const recurringEventId =
        manager.properties?.custom?.['recurringEventId']?.value;
      if (!recurringEventId) {
        return undefined;
      }
      return manager.intrinsicMeta?.title || undefined;
    }, [collection, manager.properties?.custom, manager.intrinsicMeta?.title]);

    const properties = manager.sorter.getOrderedItems();

    useEffect(() => {
      const header = headerRef.current;
      if (!header) return;

      const viewContainer = header.closest(
        '[data-view-id]'
      ) as HTMLElement | null;
      const target = viewContainer || document.body;

      const updateWidth = (width: number) => {
        if (!Number.isFinite(width) || width <= 0) return;
        const nextWidth = Math.round(width);
        if (containerWidthRef.current === nextWidth) return;
        containerWidthRef.current = nextWidth;
        setShareCompact(nextWidth < SHARE_COMPACT_BREAKPOINT);
        setHideCollection(nextWidth < COLLECTION_HIDE_BREAKPOINT);
      };

      updateWidth(target.getBoundingClientRect().width);

      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const boxSize = entry.contentBoxSize
            ? Array.isArray(entry.contentBoxSize)
              ? entry.contentBoxSize[0]
              : entry.contentBoxSize
            : null;
          const width = boxSize?.inlineSize ?? entry.contentRect.width;
          updateWidth(width);
        }
      });

      resizeObserver.observe(target);
      return () => resizeObserver.disconnect();
    }, []);

    const isShareCompact = isMobileViewValue || shareCompact;
    const isCollectionHidden = isMobileViewValue || hideCollection;

    return (
      <div
        ref={headerRef}
        className={clsx(styles.tableHeader, className)}
        style={style}
      >
        <PagePropertiesMobileHeader />
        {!isMobileViewValue && (
          <div className={styles.tableHeaderInfoRow}>
            <div className={styles.tableHeaderInfoGroup}>
              <div className={styles.tableHeaderTimestampWrapper}>
                <CalendarDaysIcon
                  className={styles.tableHeaderCollectionIcon}
                />
                <TimestampDisplay
                  docId={manager.pageId}
                  createDate={manager.createDate}
                  updatedDate={manager.updatedDate}
                />
              </div>
              {(collection || suggestedCollectionName) &&
              !isCollectionHidden ? (
                <>
                  <span className={styles.tableHeaderDot}>·</span>
                  <CollectionMenu
                    pageId={manager.pageId}
                    currentCollection={collection}
                    suggestedName={suggestedCollectionName}
                  >
                    <div
                      className={styles.tableHeaderCollection}
                      data-selected={!!collection}
                      data-suggested={!collection && !!suggestedCollectionName}
                    >
                      <FolderOpenIcon
                        className={styles.tableHeaderCollectionIcon}
                      />
                      {collection ? (
                        collection.name
                      ) : suggestedCollectionName ? (
                        <>
                          <span className={styles.tableHeaderSuggestedName}>
                            {suggestedCollectionName}
                          </span>
                          <span className={styles.tableHeaderSuggestedPlus}>
                            +
                          </span>
                        </>
                      ) : (
                        <span className={styles.tableHeaderPlaceholder}>
                          {t['Collections']()}...
                        </span>
                      )}
                    </div>
                  </CollectionMenu>
                  <span className={styles.tableHeaderDot}>·</span>
                </>
              ) : (
                <span className={styles.tableHeaderDot}>·</span>
              )}
              <AttendeesValue />
              {manager.page && (
                <SharePageButton
                  workspace={manager.workspace}
                  page={manager.page}
                  compact={isShareCompact}
                />
              )}
            </div>
            {backlinks.length > 0 ? (
              <PageBacklinksPopup backlinks={backlinks}>
                <div className={styles.tableHeaderBacklinksHint}>
                  {t['com.pique.page-properties.backlinks']()} ·{' '}
                  {backlinks.length}
                </div>
              </PageBacklinksPopup>
            ) : null}
          </div>
        )}

        {settings.displayDocInfo &&
          properties.length > 0 &&
          !manager.readonly && (
            <div className={styles.tableHeaderSecondaryRow}>
              <PagePropertiesSettingsPopup>
                <IconButton data-testid="page-info-show-more" size="20">
                  <MoreHorizontalIcon />
                </IconButton>
              </PagePropertiesSettingsPopup>
            </div>
          )}
        <Divider />
      </div>
    );
  }
);

interface PagePropertyRowProps {
  property: PageInfoCustomProperty;
  style?: React.CSSProperties;
  rowNameClassName?: string;
}

export const PagePropertyRow = ({
  property,
  rowNameClassName,
}: PagePropertyRowProps) => {
  const manager = useManager();
  const meta = manager.getCustomPropertyMeta(property.id);

  const [editingMeta, setEditingMeta] = useState(false);
  const setEditingItem = useSetAtom(editingPropertyAtom);
  const handleEditMeta = useCallback(() => {
    if (!manager.readonly) {
      setEditingMeta(true);
    }
    setEditingItem(property.id);
  }, [manager.readonly, property.id, setEditingItem]);
  const handleFinishEditingMeta = useCallback(() => {
    setEditingMeta(false);
    setEditingItem(null);
  }, [setEditingItem]);

  if (!meta) {
    return null;
  }

  const Icon = nameToIcon(meta.icon, meta.type);
  const name = meta.name;
  const ValueRenderer = propertyValueRenderers[meta.type];

  return (
    <SortablePropertyRow property={property} data-testid="page-property-row">
      {({ attributes, listeners }) => (
        <>
          <PagePropertyRowNameMenu
            editing={editingMeta}
            meta={meta}
            property={property}
            onFinishEditing={handleFinishEditingMeta}
          >
            <div
              {...attributes}
              {...listeners}
              data-testid="page-property-row-name"
              className={clsx(
                styles.sortablePropertyRowNameCell,
                rowNameClassName
              )}
              onClick={handleEditMeta}
            >
              <div className={styles.propertyRowNameContainer}>
                <div className={styles.propertyRowIconContainer}>
                  <Icon />
                </div>
                <div className={styles.propertyRowName}>{name}</div>
              </div>
            </div>
          </PagePropertyRowNameMenu>
          <ValueRenderer meta={meta} property={property} />
        </>
      )}
    </SortablePropertyRow>
  );
};

export const PageTagsRow = memo(function PageTagsRow({
  rowNameClassName,
}: {
  rowNameClassName?: string;
}) {
  const tagList = useService(TagService).tagList;
  const manager = useManager();
  const tagIds = useLiveData(tagList.tagIdsByPageId$(manager.pageId));
  const empty = !tagIds || tagIds.length === 0;

  if (empty) {
    return null;
  }

  return (
    <div
      className={styles.tagsPropertyRow}
      data-testid="page-property-row"
      data-property="tags"
    >
      <div
        className={clsx(styles.propertyRowNameCell, rowNameClassName)}
        data-testid="page-property-row-name"
      >
        <div className={styles.propertyRowNameContainer}>
          <div className={styles.propertyRowIconContainer}>
            <TagsIcon />
          </div>
        </div>
      </div>
      <TagsValue />
    </div>
  );
});

export const PagePropertiesTableBody = memo(function PagePropertiesTableBody({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  const manager = useManager();
  return (
    <div className={clsx(styles.tableBodyRoot, className)} style={style}>
      <PageTagsRow />
      <PageOverviewRow />
      <SortableProperties>
        {properties =>
          properties.length ? (
            <div className={styles.tableBodySortable}>
              {properties
                .filter(
                  property =>
                    manager.isPropertyRequired(property.id) ||
                    (property.visibility !== 'hide' &&
                      !(
                        property.visibility === 'hide-if-empty' &&
                        !property.value
                      ))
                )
                .map(property => (
                  <PagePropertyRow key={property.id} property={property} />
                ))}
            </div>
          ) : null
        }
      </SortableProperties>
    </div>
  );
});

interface PagePropertiesCreatePropertyMenuItemsProps {
  onCreated?: (e: React.MouseEvent, id: string) => void;
  metaManager: PagePropertiesMetaManager;
}

const findNextDefaultName = (name: string, allNames: string[]): string => {
  const nameExists = allNames.includes(name);
  if (nameExists) {
    const match = name.match(/(\d+)$/);
    if (match) {
      const matchedNum = match[1];
      if (matchedNum) {
        const num = parseInt(matchedNum, 10);
        const nextName = name.replace(/(\d+)$/, `${num + 1}`);
        return findNextDefaultName(nextName, allNames);
      }
      return findNextDefaultName(`${name} 2`, allNames);
    } else {
      return findNextDefaultName(`${name} 2`, allNames);
    }
  } else {
    return name;
  }
};

export const PagePropertiesCreatePropertyMenuItems = ({
  onCreated,
  metaManager,
}: PagePropertiesCreatePropertyMenuItemsProps) => {
  const t = useI18n();
  const onAddProperty = useCallback(
    (
      e: React.MouseEvent,
      option: { type: PagePropertyType; name: string; icon: string }
    ) => {
      const schemaList = metaManager.getOrderedPropertiesSchema();
      const nameExists = schemaList.some(meta => meta.name === option.name);
      const allNames = schemaList.map(meta => meta.name);
      const name = nameExists
        ? findNextDefaultName(option.name, allNames)
        : option.name;
      const { id } = metaManager.addPropertyMeta({
        name,
        icon: option.icon,
        type: option.type,
      });
      onCreated?.(e, id);
    },
    [metaManager, onCreated]
  );

  return useMemo(() => {
    const options: MenuItemOption[] = [];
    options.push(
      <div role="heading" className={styles.menuHeader}>
        {t['com.pique.page-properties.create-property.menu.header']()}
      </div>
    );
    options.push('-');
    options.push(
      newPropertyTypes.map(type => {
        const iconName = getDefaultIconName(type);
        const Icon = nameToIcon(iconName, type);
        const name = `Add ${type}`;
        return {
          icon: <Icon />,
          text: name,
          onClick: (e: React.MouseEvent) => {
            onAddProperty(e, {
              icon: iconName,
              name: name,
              type: type,
            });
          },
        };
      })
    );
    return renderMenuItemOptions(options);
  }, [onAddProperty, t]);
};

interface PagePropertiesAddPropertyMenuItemsProps {
  onCreateClicked: (e: React.MouseEvent) => void;
}

const PagePropertiesAddPropertyMenuItems = ({
  onCreateClicked,
}: PagePropertiesAddPropertyMenuItemsProps) => {
  const manager = useManager();

  const t = useI18n();
  const metaList = manager.metaManager.getOrderedPropertiesSchema();
  const nonRequiredMetaList = metaList.filter(meta => !meta.required);
  const isChecked = useCallback(
    (m: string) => {
      return manager.hasCustomProperty(m);
    },
    [manager]
  );

  const onClickProperty = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.stopPropagation();
      e.preventDefault();
      if (isChecked(id)) {
        manager.removeCustomProperty(id);
      } else {
        manager.addCustomProperty(id);
      }
    },
    [isChecked, manager]
  );

  const menuItems = useMemo(() => {
    const options: MenuItemOption[] = [];
    options.push(
      <div role="heading" className={styles.menuHeader}>
        {t['com.pique.page-properties.add-property.menu.header']()}
      </div>
    );

    if (nonRequiredMetaList.length > 0) {
      options.push('-');
      const nonRequiredMetaOptions: MenuItemOption = nonRequiredMetaList.map(
        meta => {
          const Icon = nameToIcon(meta.icon, meta.type);
          const name = meta.name;
          return {
            icon: <Icon />,
            text: name,
            selected: isChecked(meta.id),
            onClick: (e: React.MouseEvent) => onClickProperty(e, meta.id),
          };
        }
      );
      options.push(nonRequiredMetaOptions);
    }
    options.push('-');
    options.push({
      icon: <PlusIcon />,
      text: t['com.pique.page-properties.add-property.menu.create'](),
      onClick: onCreateClicked,
    });

    return renderMenuItemOptions(options);
  }, [isChecked, nonRequiredMetaList, onClickProperty, onCreateClicked, t]);

  return menuItems;
};

export const PagePropertiesAddProperty = () => {
  const t = useI18n();
  const [adding, setAdding] = useState(true);
  const manager = useManager();
  const toggleAdding: MouseEventHandler = useCallback(e => {
    e.stopPropagation();
    e.preventDefault();
    setAdding(prev => !prev);
  }, []);

  const menuOptions = useMemo(() => {
    const handleCreated = (e: React.MouseEvent, id: string) => {
      toggleAdding(e);
      manager.addCustomProperty(id);
    };
    const items = adding ? (
      <PagePropertiesAddPropertyMenuItems onCreateClicked={toggleAdding} />
    ) : (
      <PagePropertiesCreatePropertyMenuItems
        metaManager={manager.metaManager}
        onCreated={handleCreated}
      />
    );

    return {
      contentOptions: {
        onClick(e: React.MouseEvent) {
          e.stopPropagation();
        },
      },
      rootOptions: {
        onOpenChange: () => setAdding(true),
      },
      items,
    } satisfies Partial<MenuProps>;
  }, [adding, manager, toggleAdding]);

  return (
    <Menu {...menuOptions}>
      <Button
        variant="plain"
        prefix={<PlusIcon />}
        className={styles.addPropertyButton}
      >
        {t['com.pique.page-properties.add-property']()}
      </Button>
    </Menu>
  );
};

const _PagePropertiesTableInner = () => {
  return (
    <div className={styles.root}>
      <div className={styles.rootCentered}>
        <PagePropertiesTableHeader />
        <PagePropertiesTableBody />
      </div>
    </div>
  );
};

const PagePropertiesTableInner = memo(_PagePropertiesTableInner);

export const usePagePropertiesManager = (
  docId: string
): PagePropertiesManager => {
  const adapter = useService(WorkspacePropertiesAdapter);
  const managerRef = useRef<PagePropertiesManager | null>(null);

  if (!managerRef.current || managerRef.current.pageId !== docId) {
    managerRef.current = new PagePropertiesManager(adapter, docId);
  }

  return managerRef.current;
};

// this is the main component that renders the page properties table at the top of the page below
// the page title
export const PagePropertiesTable = ({ docId }: { docId: string }) => {
  const manager = usePagePropertiesManager(docId);

  useEffect(() => {
    if (manager.page) {
      manager.ensureRequiredProperties();
    }
  }, [manager]);

  return (
    <ManagerContext value={manager}>
      <Suspense>
        <PagePropertiesTableInner />
      </Suspense>
    </ManagerContext>
  );
};
